import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';
import SmokingRoomsRoundedIcon from '@mui/icons-material/SmokingRoomsRounded';
import PauseCircleRoundedIcon from '@mui/icons-material/PauseCircleRounded';
import dayjs from 'dayjs';
import { io } from 'socket.io-client';
import { baseURL } from '../../../config';
import timeTrackerController, { ITimeTrackingLiveEmployee } from '../../../Api/TimeTracker';

export type LiveViewProps = {
  onSelectEmployee: (employeeId: string, employeeName: string | null) => void;
};

const formatMs = (ms: number) => {
  if (!Number.isFinite(ms) || ms <= 0) return '00:00';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const two = (n: number) => String(n).padStart(2, '0');
  if (hours > 0) return `${two(hours)}:${two(minutes)}:${two(seconds)}`;
  return `${two(minutes)}:${two(seconds)}`;
};

const LiveView: FC<LiveViewProps> = ({ onSelectEmployee }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [employees, setEmployees] = useState<ITimeTrackingLiveEmployee[]>([]);
  const [dayKey, setDayKey] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [dataFetchedAtMs, setDataFetchedAtMs] = useState<number>(() => Date.now());
  const [tickNowMs, setTickNowMs] = useState<number>(() => Date.now());

  const refreshTimerRef = useRef<number | null>(null);

  const fetchLive = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const res = await timeTrackerController.getLive();
    if (!res.ok) {
      setEmployees([]);
      setDayKey('');
      setError(res.message || 'Nie udało się pobrać live');
      setIsLoading(false);
      return;
    }

    setEmployees(res.employees || []);
    setDayKey(res.dayKey || '');
    setDataFetchedAtMs(Date.now());
    setIsLoading(false);
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => {
      setTickNowMs(Date.now());
    }, 1000);
    return () => window.clearInterval(id);
  }, []);

  const employeesLive = useMemo<ITimeTrackingLiveEmployee[]>(() => {
    const deltaMs = Math.max(0, tickNowMs - dataFetchedAtMs);

    return employees.map((e) => {
      const presenceDurationMs = e.presence.open ? e.presence.durationMs + deltaMs : e.presence.durationMs;

      const przerwaCurrentMs = e.activities.przerwa.open ? e.activities.przerwa.currentMs + deltaMs : e.activities.przerwa.currentMs;
      const papierosCurrentMs = e.activities.papieros.open ? e.activities.papieros.currentMs + deltaMs : e.activities.papieros.currentMs;
      const piecCurrentMs = e.activities.piec.open ? e.activities.piec.currentMs + deltaMs : e.activities.piec.currentMs;

      return {
        ...e,
        presence: {
          ...e.presence,
          durationMs: presenceDurationMs,
        },
        activities: {
          ...e.activities,
          przerwa: { ...e.activities.przerwa, currentMs: przerwaCurrentMs },
          papieros: { ...e.activities.papieros, currentMs: papierosCurrentMs },
          piec: { ...e.activities.piec, currentMs: piecCurrentMs },
        },
      };
    });
  }, [employees, tickNowMs, dataFetchedAtMs]);

  const scheduleRefresh = useCallback(() => {
    if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = window.setTimeout(() => {
      fetchLive();
    }, 800);
  }, [fetchLive]);

  useEffect(() => {
    fetchLive();
  }, [fetchLive]);

  useEffect(() => {
    const socket = io(baseURL, { transports: ['websocket', 'polling'] });

    socket.on('timeTracking:newScan', () => {
      // Nie liczymy tu "na oko" – odświeżamy live bez przeładowania strony
      scheduleRefresh();
    });

    return () => {
      if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current);
      socket.disconnect();
    };
  }, [scheduleRefresh]);

  const employeeKey = useCallback((e: ITimeTrackingLiveEmployee) => {
    if (e.employeeId) return `emp:${String(e.employeeId)}`;
    const uids = Array.isArray(e.uids) ? e.uids.map((u) => String(u).trim().toUpperCase()).filter(Boolean) : [];
    return `uids:${uids.join('|')}`;
  }, []);

  const [presenceFlashByKey, setPresenceFlashByKey] = useState<Record<string, 'in' | 'out'>>({});
  const prevPresentByKeyRef = useRef<Map<string, boolean>>(new Map());
  const flashTimersRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    const prev = prevPresentByKeyRef.current;
    const next = new Map<string, boolean>();
    const nextFlashes: Record<string, 'in' | 'out'> = {};

    for (const e of employees) {
      const key = employeeKey(e);
      next.set(key, !!e.present);
      if (prev.has(key) && prev.get(key) !== !!e.present) {
        nextFlashes[key] = e.present ? 'in' : 'out';
      }
    }

    prevPresentByKeyRef.current = next;
    const changedKeys = Object.keys(nextFlashes);
    if (!changedKeys.length) return;

    setPresenceFlashByKey((current) => ({ ...current, ...nextFlashes }));
    for (const key of changedKeys) {
      const existing = flashTimersRef.current.get(key);
      if (existing) window.clearTimeout(existing);
      const id = window.setTimeout(() => {
        setPresenceFlashByKey((current) => {
          if (!current[key]) return current;
          const { [key]: _removed, ...rest } = current;
          return rest;
        });
        flashTimersRef.current.delete(key);
      }, 1100);
      flashTimersRef.current.set(key, id);
    }
  }, [employees, employeeKey]);

  const present = useMemo(() => employeesLive.filter((e) => e.present), [employeesLive]);
  const absent = useMemo(() => employeesLive.filter((e) => !e.present), [employeesLive]);

  const renderPersonTile = (e: ITimeTrackingLiveEmployee) => {
    const key = employeeKey(e);
    const name = e.employeeName || (e.uids?.[0] ? `Karta: ${e.uids[0]}` : 'Nieznany');
    const canOpen = !!e.employeeId;

    const basePresenceBg = e.present
      ? alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.14 : 0.07)
      : alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.12 : 0.06);

    const flashDir = presenceFlashByKey[key] || null;
    const flashColor = flashDir
      ? flashDir === 'in'
        ? alpha(theme.palette.success.main, 0.55)
        : alpha(theme.palette.error.main, 0.55)
      : null;
    const flashBg = flashDir
      ? flashDir === 'in'
        ? alpha(theme.palette.success.main, 0.14)
        : alpha(theme.palette.error.main, 0.12)
      : null;
    const flashKeyframesName = flashDir ? `presenceFlash_${key.replace(/[^a-zA-Z0-9_-]/g, '_')}` : '';

    const startedAt = e.presence.startedAt ? dayjs(e.presence.startedAt) : null;
    const startedAtLabel = startedAt && startedAt.isValid() ? startedAt.format('HH:mm') : '';
    const presenceLabel = e.present
      ? `W pracy${startedAtLabel ? ` od ${startedAtLabel}` : ''}`
      : 'Poza pracą';

    const activityCard = (
      key: string,
      label: string,
      icon: JSX.Element,
      open: boolean,
      currentMs: number,
      totalMs: number,
      color: 'warning' | 'error' | 'info'
    ) => {
      const shownMs = open ? currentMs : totalMs;
      const tint = theme.palette[color].main;

      return (
        <Box
          key={key}
          sx={{
            flex: '1 1 120px',
            minWidth: 120,
            borderRadius: 3,
            px: 1.25,
            py: 1,
            border: '1px solid',
            borderColor: open ? alpha(tint, 0.35) : alpha(theme.palette.text.primary, 0.08),
            backgroundColor: open ? alpha(tint, 0.10) : alpha(theme.palette.text.primary, 0.02),
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
            <Stack direction="row" alignItems="center" gap={0.75} sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 99,
                  display: 'grid',
                  placeItems: 'center',
                  color: open ? tint : alpha(theme.palette.text.primary, 0.55),
                  backgroundColor: open ? alpha(tint, 0.12) : alpha(theme.palette.text.primary, 0.05),
                  flex: '0 0 auto',
                }}
              >
                {icon}
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" fontWeight={900} sx={{ letterSpacing: '0.02em' }} noWrap>
                  {label}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }} noWrap>
                  {open ? 'Trwa' : 'Suma'}
                </Typography>
              </Box>
            </Stack>

            <Typography
              variant="caption"
              fontWeight={950}
              sx={{
                letterSpacing: '-0.01em',
                color: open ? tint : theme.palette.text.primary,
              }}
              noWrap
            >
              {formatMs(shownMs)}
            </Typography>
          </Stack>
        </Box>
      );
    };

    const statusDot = e.present ? theme.palette.success.main : alpha(theme.palette.error.main, 0.65);
    const statusLabel = e.present ? 'Obecny' : 'Nieobecny';

    return (
      <ButtonBase
        onClick={() => {
          if (!e.employeeId) return;
          onSelectEmployee(e.employeeId, e.employeeName || null);
        }}
        disabled={!canOpen}
        sx={{
          textAlign: 'left',
          borderRadius: 3,
          width: '100%',
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            p: 1.75,
            borderRadius: 3,
            width: '100%',
            minHeight: 156,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            position: 'relative',
            overflow: 'hidden',
            borderColor: (theme) =>
              e.present ? alpha(theme.palette.success.main, 0.28) : alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.22 : 0.18),
            backgroundColor: basePresenceBg,
            transition: 'transform 140ms ease, box-shadow 140ms ease, border-color 220ms ease',
            ...(canOpen
              ? {
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: 2,
                  },
                }
              : null),
            ...(flashDir
              ? {
                  backgroundColor: flashBg as any,
                  animation: `${flashKeyframesName} 950ms ease-in-out`,
                  [`@keyframes ${flashKeyframesName}`]: {
                    '0%': {
                      transform: 'scale(1)',
                      boxShadow: `0 0 0 0 ${flashColor}`,
                    },
                    '35%': {
                      transform: 'scale(1.006)',
                      boxShadow: `0 0 0 6px ${alpha(flashColor as string, 0.0)}`,
                    },
                    '70%': {
                      transform: 'scale(1)',
                      boxShadow: `0 0 0 0 ${alpha(flashColor as string, 0.0)}`,
                    },
                    '100%': {
                      transform: 'scale(1)',
                      boxShadow: `0 0 0 0 ${alpha(flashColor as string, 0.0)}`,
                    },
                  },
                }
              : null),
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={950} sx={{ letterSpacing: '-0.012em', lineHeight: 1.15 }} noWrap>
                {name}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2 }} noWrap>
                {presenceLabel}
              </Typography>
            </Box>

            <Stack direction="row" alignItems="center" gap={0.75} sx={{ pt: 0.15 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: statusDot }} />
              <Typography variant="caption" fontWeight={900} color="text.secondary" sx={{ letterSpacing: '0.06em' }} noWrap>
                {statusLabel.toUpperCase()}
              </Typography>
            </Stack>
          </Stack>

          <Stack direction="row" alignItems="baseline" justifyContent="space-between" gap={1}>
            <Typography variant="h5" fontWeight={950} sx={{ lineHeight: 1.02, letterSpacing: '-0.02em' }}>
              {e.present ? formatMs(e.presence.durationMs) : '--:--'}
            </Typography>
            {startedAtLabel ? (
              <Typography variant="caption" color="text.secondary" noWrap>
                od {startedAtLabel}
              </Typography>
            ) : null}
          </Stack>

          <Box sx={{ height: 1, bgcolor: 'divider', opacity: 0.85 }} />

          <Stack direction="row" gap={1} flexWrap="wrap">
            {activityCard(
              'przerwa',
              'Przerwa',
              <PauseCircleRoundedIcon sx={{ fontSize: 18 }} />,
              e.activities.przerwa.open,
              e.activities.przerwa.currentMs,
              e.activities.przerwa.totalMs,
              'warning'
            )}
            {activityCard(
              'papieros',
              'Papieros',
              <SmokingRoomsRoundedIcon sx={{ fontSize: 18 }} />,
              e.activities.papieros.open,
              e.activities.papieros.currentMs,
              e.activities.papieros.totalMs,
              'error'
            )}
            {activityCard(
              'piec',
              'Piec',
              <LocalFireDepartmentRoundedIcon sx={{ fontSize: 18 }} />,
              e.activities.piec.open,
              e.activities.piec.currentMs,
              e.activities.piec.totalMs,
              'info'
            )}
          </Stack>

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" color="text.secondary" noWrap>
              Karty: {typeof e.uids?.length === 'number' && e.uids.length > 0 ? e.uids.length : '-'}
            </Typography>
            {canOpen ? (
              <Typography variant="caption" color="text.secondary" noWrap>
                Raport →
              </Typography>
            ) : null}
          </Stack>
        </Paper>
      </ButtonBase>
    );
  };

  const listSx = {
    display: 'grid',
    gridTemplateColumns: {
      xs: '1fr',
      sm: 'repeat(2, minmax(0, 1fr))',
      md: 'repeat(auto-fit, minmax(320px, 1fr))',
    },
    gap: 1.25,
  };

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} gap={2}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={800}>
              Live
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Dzień: {dayKey || dayjs().format('YYYY-MM-DD')} • Obecni: {present.length} • Nieobecni: {absent.length}
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
            onClick={fetchLive}
            disabled={isLoading}
            fullWidth={!isDesktop}
          >
            Odśwież
          </Button>
        </Stack>

        {error ? (
          <Box sx={{ mt: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : null}

        <Divider sx={{ my: 2 }} />

        <Box sx={listSx}>
          {present.map((e) => (
            <Box key={employeeKey(e)}>
              {renderPersonTile(e)}
            </Box>
          ))}
        </Box>

        {absent.length ? (
          <Box sx={{ mt: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Nieobecni
            </Typography>
            <Box sx={{ ...listSx, mt: 0.75 }}>
              {absent.map((e) => (
                <Box key={employeeKey(e)}>
                  {renderPersonTile(e)}
                </Box>
              ))}
            </Box>
          </Box>
        ) : null}
      </Paper>
    </Box>
  );
};

export default LiveView;
